import { BLOCKS, INLINES } from '@contentful/rich-text-types';
import { FormattedMessage, useIntl } from 'gatsby-plugin-react-intl';
import PropTypes from 'prop-types';
import { useContext, useEffect, useMemo, useState } from 'react';
import ReactPlayer from 'react-player';
import uuid from 'react-uuid';

import { isNotEmpty } from '../../../utils';
import Container from '../../Core/Container';
import Link from '../../Core/Link';
import RichText, { fragment } from '../../Core/RichText';
import IndividualBlade from '../../Custom/IndividualBlades';
import ProductIconText from '../../Custom/Optimum/OptimumProductCards/ProductIconText';
import { ResponsiveContext } from '../../Providers/Responsive';
import InterestsValueComponent from '../../Providers/Core/Services/InterestValue';
import AddToCalendarCta from '../../Core/Ctas/AddToCalendarCta';
import Pills from '../../Core/Pills';

import {
  Content,
  CTAWrapper,
  Heading,
  HeroImage,
  Intro,
  MediaContainer,
  PausePlayBtn,
  Points,
  PointsContainer,
  PreTitle,
  SubHead,
  SubTitle,
  Title,
  VideoContainer,
  Wrapper,
} from './styles';

const isImg = media => {
  if (!media) return false;
  const isImage = useMemo(
    () =>
      media.asset.mimeType
        ? /^(.*?(\bimage\b)[^$]*)$/.test(media.asset.mimeType)
        : false,
    [media],
  );
  return isImage;
};

const HeroScreen = ({
  data,
  objectFit = 'contain',
  lang,
  bgImage = '',
  bgSize = null,
  assetPosition = false,
  theme = '',
  intl,
}) => {
  const { asset, altText } = data;

  const isImage = isImg(data);

  // * state to track if the video has started playing or not
  const [playing, setPlaying] = useState(false);

  const handlePlay = () => {
    setPlaying(!playing);
  };

  const [hasWindow, setHasWindow] = useState(false);
  useEffect(() => {
    if (typeof window !== 'undefined') {
      setHasWindow(true);
    }
  }, []);

  return useMemo(() => {
    return (
      asset.mimeType && (
        <MediaContainer
          bgImage={bgImage}
          bgSize={bgSize}
          assetPosition={assetPosition}
          className={`${theme} ${isImage ? 'img' : 'video'}`}
          lang={lang}
        >
          {isImage ? (
            <HeroImage
              $objectFit={objectFit}
              src={data}
              alt={altText || ''}
              loading="eager"
            />
          ) : (
            <VideoContainer>
              {hasWindow && (
                <ReactPlayer
                  className="react-player"
                  width="100%"
                  height="100%"
                  url={asset.localImage.publicURL}
                  playing={playing}
                  playsinline
                  muted
                  loop
                  controls={false}
                />
              )}
              <PausePlayBtn
                playing={playing}
                onClick={handlePlay}
                aria-label={
                  playing ? intl.messages?.pause : intl.messages?.play
                }
              />
            </VideoContainer>
          )}
        </MediaContainer>
      )
    );
  }, [asset, hasWindow, playing]);
};

HeroScreen.propTypes = {
  data: PropTypes.shape().isRequired,
  lang: PropTypes.string.isRequired,
  objectFit: PropTypes.string,
  bgImage: PropTypes.string,
  bgSize: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.string,
  ]),
  assetPosition: PropTypes.bool,
  theme: PropTypes.string,
  intl: PropTypes.object,
};

const DesktopAndImageSplit = ({
  data,
  objectFit,
  lang,
  bgImage = '',
  bgSize = null,
  assetPosition = false,
  theme = '',
}) => {
  const [desktopIMG, mobileIMG] = data;

  const { mobile } = useContext(ResponsiveContext);

  const media = mobile ? mobileIMG : desktopIMG;

  return useMemo(() => {
    return (
      media && (
        <MediaContainer
          bgImage={bgImage}
          bgSize={bgSize}
          assetPosition={assetPosition}
          className={`${objectFit} ${theme}`}
          lang={lang}
        >
          <HeroImage
            objectFit={objectFit}
            src={media}
            alt={media.altText || ''}
            loading="eager"
          />
        </MediaContainer>
      )
    );
  }, [media]);
};

DesktopAndImageSplit.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  lang: PropTypes.string.isRequired,
  objectFit: PropTypes.string.isRequired,
  bgImage: PropTypes.string,
  bgSize: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.string,
  ]),
  assetPosition: PropTypes.bool,
  theme: PropTypes.string,
};

/**
 *
 * * Contentful RTE returns empty <p/> tag inside of an array which is a child of an array. Paragraph tags are being converted to <h1/>'s which was resulting in duplicate heading tags, one of which was empty resulting in an accesibility violation.
 * * Function checks initial array for a child, if the child has length -> function checks if the child is a string or an empty array. If result is an empty array, return null otherwise display content.
 *  TODO - hoist function to a higher level so that it may be used globally on all RTE's
 */

const Hero = ({ data, bg = null, media = null, className = '' }) => {
  if (!data) return null;
  const intl = useIntl();

  const richTextContentAsTitle = (_, children) =>
    isNotEmpty(children) ? (
      <Title as="h1" lang={intl.locale}>
        {children}
      </Title>
    ) : null;

  // Format for preheading
  const richTextContentAsPreTitle = (_, children) =>
    isNotEmpty(children) ? <PreTitle>{children}</PreTitle> : null;

  // Format for postheading
  const richTextContentAsSubTitle = (_, children) =>
    isNotEmpty(children) ? <SubTitle>{children}</SubTitle> : null;

  const title = {
    renderNode: {
      [BLOCKS.HEADING_1]: richTextContentAsTitle,
      [INLINES.EMBEDDED_ENTRY]: node => {
        const { data: segment } = node;
        if (segment.target.__typename === 'ContentfulCoreApi') {
          return <InterestsValueComponent data={segment.target} />;
        }
        return null;
      },
    },
  };

  const preTitle = {
    renderNode: {
      [BLOCKS.PARAGRAPH]: richTextContentAsPreTitle,
      [INLINES.EMBEDDED_ENTRY]: node => {
        const { data: segment } = node;
        if (segment.target.__typename === 'ContentfulCoreApi') {
          return <InterestsValueComponent data={segment.target} />;
        }
        return null;
      },
    },
  };

  const subTitle = {
    renderNode: {
      [BLOCKS.PARAGRAPH]: richTextContentAsSubTitle,
      [INLINES.EMBEDDED_ENTRY]: node => {
        const { data: segment } = node;
        if (segment.target.__typename === 'ContentfulCoreApi') {
          return <InterestsValueComponent data={segment.target} />;
        }
        return null;
      },
    },
  };

  const subhead = {
    renderNode: {
      [BLOCKS.PARAGRAPH]: (_, children) => {
        return <p>{children}</p>;
      },
      [BLOCKS.EMBEDDED_ENTRY]: node => {
        const { data: segment } = node;
        if (
          segment.target.__typename === 'ContentfulTitleOrImageWithIconLists'
        ) {
          return <ProductIconText data={segment.target} className="hero" />;
        }
        return null;
      },
      [INLINES.EMBEDDED_ENTRY]: node => {
        const { data: segment } = node;
        if (segment.target.__typename === 'ContentfulCoreApi') {
          return <InterestsValueComponent data={segment.target} />;
        }
        return null;
      },
    },
  };

  const {
    component,
    backgroundAssetDam,
    backgroundAssetPosition,
    backgroundAssetSize,
    backgroundColor,
    backgroundColorWidth,
    textAlignment,
    preheading,
    heading,
    points,
    pointsColor,
    postHeading,
    subheading,
    ctas,
    contentBackgroundColor,
    mediaimageDam,
    objectFit,
    theme,
    hideMobile,
    pillText,
    pillBackgroundColor,
    pillTextColor,
    pillIcon,
  } = data;

  const { mobile } = useContext(ResponsiveContext);

  /**
   * * Pull background colors from themes file and map to match CMS values.
   */
  // const wrapperBgColor = themeGet(`colors.${backgroundColor}`);

  /**
   * * Check if `backgroundAssetSize` has no value, if so set to 'cover' as default, otherwise split the string into 3 separate values inside an array. The values then get passed to the wrapper and use as `the background-size` in % in the following order: mobile, tablet (md) and desktop(lg).
   */
  const bgSize =
    backgroundAssetSize === null ? 'cover' : backgroundAssetSize.split(/[,]+/);

  /**
   * * Check if `backgroundAssetPosition` is returning a value and change to a boolean value. Use this value to determine where the asset and background styling should be applied, either to the wrapper or the image-wrapper.
   */
  const assetPosition = !!backgroundAssetPosition;

  /**
   * * Check if `backgroundColorWidth` is returning a value and change to a boolean value. Use this value to determine which div the background color should be on.
   */
  const bgColorWidth = !!backgroundColorWidth;

  const generateCtaAlignment = alignment => {
    switch (alignment) {
      case 'center':
        return 'center';
      case 'right':
        return 'flex-end';
      case 'left':
      default:
        return 'flex-start';
    }
  };

  const ctaAlignment = generateCtaAlignment(textAlignment);

  // checks whether background image should be against wrapper or full width
  const backgroundAssessment = bgColorWidth && !!backgroundAssetDam === true;

  // check whether media asset is a video or image
  const isImage = mediaimageDam ? isImg(mediaimageDam[0]) : false;

  // if mobile = true and hero mobile is true don't render image
  // if mobile = true and hero mobile is false render image
  // if mobile = false and hero mobile is false render image
  // if mobile = false and hero mobile is true render image
  const mobileHeroImageResolver = () => {
    if (mobile && hideMobile) {
      return null;
    }
    return (
      <>
        {media ||
          (mediaimageDam &&
            (mediaimageDam.length === 1 ? (
              <HeroScreen
                theme={theme}
                data={mediaimageDam[0]}
                objectFit={objectFit}
                lang={intl.locale}
                bgImage={
                  backgroundAssetDam && assetPosition === true
                    ? backgroundAssetDam[0].asset.localImage.publicURL
                    : null
                }
                bgSize={bgSize}
                assetPosition={assetPosition}
                intl={intl}
              />
            ) : (
              <DesktopAndImageSplit
                theme={theme}
                data={mediaimageDam}
                objectFit={objectFit}
                lang={intl.locale}
                bgImage={
                  backgroundAssetDam && assetPosition === true
                    ? backgroundAssetDam[0].asset.localImage.publicURL
                    : null
                }
                bgSize={bgSize}
                assetPosition={assetPosition}
              />
            )))}
      </>
    );
  };

  return (
    <Container
      as="section"
      type="hero"
      className={`${className} ${component} ${theme}`}
      backgroundImage={backgroundAssessment ? backgroundAssetDam[0] : null}
      backgroundColor={
        backgroundColor && bgColorWidth === true ? backgroundColor : null
      }
    >
      <Wrapper
        data-testid="hero-banner"
        className={`${component} ${theme} ${isImage ? 'img' : 'video'}`}
        image={
          backgroundAssetDam &&
          assetPosition === false &&
          bgColorWidth === false
            ? backgroundAssetDam[0].asset.localImage.publicURL
            : null
        }
        backgroundColor={
          backgroundColor && bgColorWidth === false ? backgroundColor : null
        }
        contentColor={backgroundColor}
        bgSize={bgSize}
        assetPosition={assetPosition}
      >
        <Content
          className={`${theme} ${isImage ? 'img' : 'video'}`}
          contentBackgroundColor={contentBackgroundColor}
          contentColor={contentBackgroundColor}
          bgColorWidth={bgColorWidth}
          assetPosition={assetPosition}
          $textAlingment={textAlignment}
        >
          {pillText && (
            <Pills
              backgroundColor={pillBackgroundColor}
              textColor={pillTextColor}
              pillIcon={pillIcon}
              className="hero"
            >
              {pillText}
            </Pills>
          )}
          {heading && !points && (
            <Heading>
              {preheading && (
                <RichText document={preheading} options={preTitle} />
              )}
              <RichText document={heading} options={title} />
            </Heading>
          )}
          {!heading && points && (
            <Points>
              {preheading && (
                <RichText document={preheading} options={preTitle} />
              )}
              <PointsContainer $ptsColor={pointsColor}>
                {points}
                <span className={`pts-bubble ${theme || 'pc-red'}`}>
                  <FormattedMessage id="pointsWord" />
                </span>
              </PointsContainer>
              {postHeading && (
                <Intro className={theme}>
                  <RichText document={postHeading} options={subTitle} />
                </Intro>
              )}
            </Points>
          )}
          {subheading && (
            <SubHead>
              <RichText document={subheading} options={subhead} />
            </SubHead>
          )}
          {ctas && (
            <CTAWrapper $ctaAlignment={ctaAlignment}>
              {ctas.map(cta => {
                if (cta.hideFrench === true) return null;
                if (cta.internal && cta.internal.type === 'ContentfulBlade') {
                  return <IndividualBlade key={uuid()} data={cta} />;
                } else if (cta.type === 'add-to-calendar') {
                  return <AddToCalendarCta key={uuid()} data={cta} />;
                }
                return (
                  <Link key={uuid()} data={cta}>
                    <RichText document={cta.label} options={fragment} />
                  </Link>
                );
              })}
            </CTAWrapper>
          )}
        </Content>
        {/* Optional Image component can be passed in for animations, or defaults to static images */}
        {mobileHeroImageResolver()}
        {/* OPTIONAL BACKGROUND ELEMENT TO BE PASSED IN FOR ANIMATIONS */}
        {bg}
        {/* OPTIONAL BACKGROUND ELEMENT TO BE PASSED IN FOR ANIMATIONS */}
      </Wrapper>
    </Container>
  );
};

Hero.propTypes = {
  data: PropTypes.shape().isRequired,
  bg: PropTypes.node,
  media: PropTypes.node,
  className: PropTypes.string,
};

export default Hero;
